import React, { useState, useEffect } from "react";
import { checkPermission } from "../../../core/permissons/CheckPermissons";
import apiDefinitions from "../../../core/api/apiDefinitions";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  showDepartmentConfirmSubmission,
  showSuccessAlert,
} from "../../../core/alert/sweetAlertConfig";
import CollapseHeader from "../../../core/common/collapse-header";

/** Types */
interface Department {
  id: number;
  name: string;
  location: string;
  address: string;
  createdBy: string;
  createdAt: string;
}

interface NewDepartment {
  name: string;
  location: string;
  address: string;
}

interface LocationDTO {
  id: number;
  name: string;
  address: string;
}

interface ApiResponseDepartments {
  status: string;
  data: {
    departments: Department[];
    total: number;
  };
}

interface ApiResponseLocations {
  data: {
    status: string;
    data: {
      locations: LocationDTO[];
      total: number;
    };
  };
}

const PAGE_SIZE = 15;

const Department: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Modal
  const [showModal, setShowModal] = useState<boolean>(false);
  const [newDepartment, setNewDepartment] = useState<NewDepartment>({
    name: "",
    location: "",
    address: "",
  });
  const [selectedLocationId, setSelectedLocationId] = useState<number | "">("");

  /** Fetch Departments */
  const fetchDepartments = async (page: number = 1, filter: string = "") => {
    try {
      const response = await apiDefinitions.getDepartments(page, {
        filterType: filter ? "NAME" : null,
        filterValue: filter,
        pageSize: PAGE_SIZE,
      });

      // AxiosResponse<any> → response.data is the actual payload

      if (response.data.status === "S1000") {
        const deptData = response.data.data;
        setDepartments(deptData.departments || []);
        setTotalItems(deptData.total || 0);
        setTotalPages(Math.ceil((deptData.total || 0) / PAGE_SIZE));
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  /** Fetch Locations */
  const fetchLocations = async () => {
    try {
      const res: ApiResponseLocations = await apiDefinitions.getLocations(1, {
        filterType: null,
        filterValue: "",
      });
      if (res?.data?.status === "S1000") {
        setLocations(res.data.data.locations || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  useEffect(() => {
    fetchDepartments(1, "");
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    fetchDepartments(1, term);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchDepartments(page, searchTerm);
  };

  const openAddModal = () => {
    setShowModal(true);
    setNewDepartment({ name: "", location: "", address: "" });
    setSelectedLocationId("");
    fetchLocations();
  };

  const handleAddDepartment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isConfirmed = await ( showDepartmentConfirmSubmission() );
    if (isConfirmed) {
      try {
        await apiDefinitions.addDepartment(newDepartment);
        showSuccessAlert("Department added successfully");
        setShowModal(false);
        fetchDepartments(currentPage, searchTerm);
      } catch (error) {
        console.error("Error adding department:", error);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewDepartment((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const locId = value ? Number(value) : "";
    setSelectedLocationId(locId);

    const loc = locations.find((l) => l.id === locId);
    if (loc) {
      setNewDepartment((prev) => ({
        ...prev,
        location: loc.name,
        address: loc.address,
      }));
    } else {
      setNewDepartment((prev) => ({ ...prev, location: "", address: "" }));
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous Button (Secondary)
    pages.push(
      <li
        key="prev"
        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
      >
        <button
          className="page-link color-secondary text-text"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
      </li>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis1" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis2" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    // Next Button (Success - always green)
    pages.push(
      <li
        key="next"
        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <button
          className="page-link btn-success text-text"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </li>
    );

    return (
      <nav aria-label="Department pagination" className="mt-4">
        <ul className="pagination justify-content-end mb-0">{pages}</ul>
      </nav>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Page Header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Administration / Department</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Add */}
            <div className="row mb-3 align-items-end">
              <div className="col-md-6">
                <Form.Group controlId="searchDepartment">
                  <Form.Label>Search Departments</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </Form.Group>
              </div>
              {checkPermission("ROLE_ADD_DEPARTMENT") && (
                <div className="col-md-6 text-end">
                  <Button variant="success" onClick={openAddModal}>
                    + Add Department
                  </Button>
                </div>
              )}
            </div>

            {/* Showing count */}
            <div className="mb-3">
              <small className="text-muted">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems}{" "}
                departments
              </small>
            </div>

            {/* Table Card */}
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">Departments of KIU</h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover datanew">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Location</th>
                        <th>Address</th>
                        <th>Created By</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5">
                            <em>No departments found.</em>
                          </td>
                        </tr>
                      ) : (
                        departments.map((dept, index) => (
                          <tr key={dept.id}>
                            <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                            <td>
                              <strong>{dept.name}</strong>
                            </td>
                            <td>{dept.location}</td>
                            <td>{dept.address}</td>
                            <td>{dept.createdBy}</td>
                            <td>
                              {new Date(dept.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination - Right aligned, colored buttons */}
                {renderPagination()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Department Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddDepartment}>
            <Form.Group className="mb-3">
              <Form.Label>Department Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newDepartment.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Department of Computer Science"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location *</Form.Label>
              <Form.Select
                value={selectedLocationId}
                onChange={handleLocationSelect}
                required
              >
                <option value="">-- Select Location --</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address (Auto-filled)</Form.Label>
              <Form.Control
                type="text"
                value={newDepartment.address}
                readOnly
                placeholder="Address will appear after selecting location"
              />
            </Form.Group>

            <div className="text-end">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="success" type="submit">
                Save Department
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Department;
